import { useQuery } from '@tanstack/react-query'

import type { OsaEnvironment } from '@/shared/types'
import { httpClient } from '@/shared/utils/http-client'

// Use relative path in development to leverage Vite proxy
const API_BASE_URL = import.meta.env.DEV
  ? '/user/v1/admin'
  : 'https://scout-service-v2.enter.de/user/v1/admin'

interface StageOverviewData {
  new: {
    all: number
    old: number
  }
  dispatched: {
    all: number
    old: number
  }
  completed: number
  cancelled: number
}

interface ScoutWithOsaCount {
  uuid: string
  email: string
  phone: string | null
  firstName: string
  lastName: string
  city: string | null
  street: string | null
  postalcode: string | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  emailVerificationAt: string | null
  status: string
  coord: {
    lat: number
    lng: number
  } | null
  osaCount: number
}

interface ScoutOsaCountResponse {
  data: ScoutWithOsaCount[]
  count: number
}

export interface OnSiteAppointment {
  uuid: string
  buildingUUID: string
  customerAccountUUID: string
  scoutUUID: string | null
  scoutName: string | null
  scheduleStartTime: string | null
  scheduleEndTime: string | null
  status: string
  statusChangedAt: string | null
  formVersion: number | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  confirmationEmailSent: string | null
  residentialUnits: number | null
  environment: string
  // Customer data fields added in scout-service-v2
  customerName: string | null
  customerAddress: string | null
  customerPhone: string | null
  customerEmail: string | null
  costPerOSA: number | null
  type: string | null
  subtypes: string[] | null
  scoutCreated: boolean | null
  // Average scout rating in percent; only present for rated completed appointments
  rating?: number
}

interface AppointmentsInRangeResponse {
  data: OnSiteAppointment[]
  count: number
}

// Lead time of appointments between two statuses. Durations are in seconds
// and zero when count is zero.
export interface StatusLeadTime {
  from: string
  to: string
  count: number
  meanSeconds: number
  medianSeconds: number
  minSeconds: number
  maxSeconds: number
}

export interface StatusLeadTimesResponse {
  createdToDispatched: StatusLeadTime
  dispatchedToCompleted: StatusLeadTime
}

class ApiError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

class AnalyticsApiClient {
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`
      let responseBody = null

      try {
        responseBody = await response.json()
        errorMessage = responseBody.message || responseBody.error || errorMessage
        console.error('API Error Response:', responseBody)
      } catch {
        // If response body is not JSON, try to get text
        try {
          const textBody = await response.text()
          console.error('API Error Text:', textBody)
          errorMessage = textBody || errorMessage
        } catch {
          // If that fails too, use default error message
        }
      }

      throw new ApiError(errorMessage, response.status)
    }

    const contentType = response.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text()
      console.error('Non-JSON response:', text)
      const jsonError = new Error(`Expected JSON but got ${contentType}: ${text.substring(0, 200)}`)
      throw jsonError
    }

    return response.json() as Promise<T>
  }

  async getStageOverview(env: OsaEnvironment = 'live'): Promise<StageOverviewData> {
    // Build URL with query parameters
    const queryParams = new URLSearchParams({ env })
    const url = `${API_BASE_URL}/analytics/projects/stage-overview?${queryParams.toString()}`

    const response = await httpClient.fetch(url, {
      method: 'GET',
    })

    return this.handleResponse<StageOverviewData>(response)
  }

  async getScoutOsaCount(fromDate?: string, toDate?: string): Promise<ScoutOsaCountResponse> {
    // Build URL with query parameters
    const queryParams = new URLSearchParams()
    if (fromDate) queryParams.append('fromDate', fromDate)
    if (toDate) queryParams.append('toDate', toDate)

    const url = `${API_BASE_URL}/analytics/scouts/osa-count${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    const response = await httpClient.fetch(url, {
      method: 'GET',
    })

    return this.handleResponse<ScoutOsaCountResponse>(response)
  }

  async getAppointmentsInRange(
    fromDate: string,
    toDate: string,
    timeColumn: string = 'scheduleStartTime',
    status?: string,
    env?: string
  ): Promise<AppointmentsInRangeResponse> {
    // Build URL with query parameters
    const queryParams = new URLSearchParams({
      fromDate,
      toDate,
      timeColumn,
    })
    if (status) queryParams.append('status', status)
    if (env) queryParams.append('env', env)

    const url = `${API_BASE_URL}/analytics/appointments/range?${queryParams.toString()}`

    const response = await httpClient.fetch(url, {
      method: 'GET',
    })

    return this.handleResponse<AppointmentsInRangeResponse>(response)
  }

  async getStatusLeadTimes(fromDate?: string, toDate?: string): Promise<StatusLeadTimesResponse> {
    // Build URL with query parameters
    const queryParams = new URLSearchParams()
    if (fromDate) queryParams.append('fromDate', fromDate)
    if (toDate) queryParams.append('toDate', toDate)

    const url = `${API_BASE_URL}/analytics/appointments/status-lead-times${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    const response = await httpClient.fetch(url, {
      method: 'GET',
    })

    return this.handleResponse<StatusLeadTimesResponse>(response)
  }
}

export const analyticsApi = new AnalyticsApiClient()

export const useStageOverview = (env: OsaEnvironment = 'live') => {
  return useQuery({
    queryKey: ['analytics', 'stage-overview', env],
    queryFn: () => analyticsApi.getStageOverview(env),
  })
}

export const useScoutOsaCount = (fromDate?: string, toDate?: string) => {
  return useQuery({
    queryKey: ['analytics', 'scout-osa-count', fromDate, toDate],
    queryFn: () => analyticsApi.getScoutOsaCount(fromDate, toDate),
  })
}

export const useAppointmentsInRange = (
  fromDate: string,
  toDate: string,
  timeColumn: string = 'scheduleStartTime',
  status?: string,
  env?: string
) => {
  return useQuery({
    queryKey: ['analytics', 'appointments-range', fromDate, toDate, timeColumn, status, env],
    queryFn: () => analyticsApi.getAppointmentsInRange(fromDate, toDate, timeColumn, status, env),
  })
}

export const useStatusLeadTimes = (fromDate?: string, toDate?: string) => {
  return useQuery({
    queryKey: ['analytics', 'status-lead-times', fromDate, toDate],
    queryFn: () => analyticsApi.getStatusLeadTimes(fromDate, toDate),
  })
}
